import { useCallback, useEffect, useState } from "react";
import {
  API_ROUTES,
  BOTTLE_CLICKER_UPGRADES,
  LIMITS,
  upgradeMaxLevel,
  type AdminBottleClickerRow,
  type BottleClickerAdminPatch,
  type BottleClickerUpgradeId,
} from "@ziolo/shared";
import { Button, Card } from "@ziolo/ui";
import { Field } from "../Field.js";
import { apiRequest, errorMessage } from "../../lib/apiClient.js";
import { useFormSubmit } from "../../hooks/useFormSubmit.js";
import { confirmAdmin } from "../../hooks/useIsAdmin.js";
import { AdminPanel } from "./AdminChrome.js";
import "../../styles/admin.css";

type Draft = {
  prestigeCount: string;
  groszeZl: string;
  lifetimeZl: string;
  runZl: string;
  returnedBottles: string;
  bestCombo: string;
  upgrades: Record<BottleClickerUpgradeId, string>;
};

function groszeToZl(grosze: number): string {
  return (grosze / 100).toFixed(2);
}

function zlToGrosze(raw: string): number | null {
  const n = Number.parseFloat(raw.replace(",", "."));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function intOrNull(raw: string): number | null {
  const n = Number.parseInt(raw, 10);
  return Number.isInteger(n) ? n : null;
}

function draftFromRow(row: AdminBottleClickerRow): Draft {
  return {
    prestigeCount: String(row.save.prestigeCount),
    groszeZl: groszeToZl(row.save.grosze),
    lifetimeZl: groszeToZl(row.save.lifetimeGrosze),
    runZl: groszeToZl(row.save.runLifetimeGrosze),
    returnedBottles: String(row.save.returnedBottles),
    bestCombo: String(row.save.bestCombo),
    upgrades: Object.fromEntries(
      BOTTLE_CLICKER_UPGRADES.map((item) => [item.id, String(row.save.upgrades[item.id])]),
    ) as Record<BottleClickerUpgradeId, string>,
  };
}

export function BottleClickerOnPageAdmin({
  onChanged,
}: {
  onChanged?: (() => void | Promise<void>) | undefined;
}) {
  return (
    <AdminPanel title="Bottle clicker">
      <BottleClickerAdminTools onChanged={onChanged} />
    </AdminPanel>
  );
}

export function BottleClickerAdminTools({
  onChanged,
}: {
  onChanged?: (() => void | Promise<void>) | undefined;
}) {
  const { busy, submit } = useFormSubmit();
  const [rows, setRows] = useState<AdminBottleClickerRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [query, setQuery] = useState("");

  const reload = useCallback(async () => {
    const list = await apiRequest<AdminBottleClickerRow[]>(API_ROUTES.admin.bottleClickerSaves);
    setRows(list);
    setDrafts(Object.fromEntries(list.map((row) => [row.userId, draftFromRow(row)])));
    setError(null);
  }, []);

  useEffect(() => {
    reload().catch((cause: unknown) => setError(errorMessage(cause)));
  }, [reload]);

  function patchDraft(userId: number, next: Partial<Draft>) {
    setDrafts((current) => {
      const draft = current[userId];
      if (!draft) return current;
      return { ...current, [userId]: { ...draft, ...next } };
    });
  }

  async function saveRow(row: AdminBottleClickerRow) {
    const draft = drafts[row.userId];
    if (!draft) return;
    const prestigeCount = intOrNull(draft.prestigeCount);
    const grosze = zlToGrosze(draft.groszeZl);
    const lifetimeGrosze = zlToGrosze(draft.lifetimeZl);
    const runLifetimeGrosze = zlToGrosze(draft.runZl);
    const returnedBottles = intOrNull(draft.returnedBottles);
    const bestCombo = intOrNull(draft.bestCombo);
    if (
      prestigeCount === null ||
      grosze === null ||
      lifetimeGrosze === null ||
      runLifetimeGrosze === null ||
      returnedBottles === null ||
      bestCombo === null
    ) {
      return;
    }
    const upgrades: NonNullable<BottleClickerAdminPatch["upgrades"]> = {};
    for (const item of BOTTLE_CLICKER_UPGRADES) {
      const level = intOrNull(draft.upgrades[item.id]);
      if (level === null) return;
      upgrades[item.id] = level;
    }
    await submit(async () => {
      const updated = await apiRequest<AdminBottleClickerRow>(
        API_ROUTES.admin.bottleClickerSave(row.userId),
        {
          method: "PATCH",
          body: {
            prestigeCount,
            grosze,
            lifetimeGrosze,
            runLifetimeGrosze,
            returnedBottles,
            bestCombo,
            upgrades,
          } satisfies BottleClickerAdminPatch,
        },
      );
      setRows((current) =>
        current?.map((item) => (item.userId === updated.userId ? updated : item)) ?? null,
      );
      setDrafts((current) => ({ ...current, [updated.userId]: draftFromRow(updated) }));
      await onChanged?.();
    }, `Zapisano ${row.username}`);
  }

  async function restartAll() {
    if (
      !confirmAdmin(
        "Wyzerować Bottle clicker wszystkich zalogowanych graczy? Kaucja, sklepy, prestiż i ranking wracają do zera. Bez żetonów kasyna.",
      )
    ) {
      return;
    }
    await submit(async () => {
      await apiRequest(API_ROUTES.admin.bottleClickerRestart, { method: "POST" });
      await reload();
      await onChanged?.();
    }, "Wyzerowano wszystkich");
  }

  if (error) return <p className="hub-error">{error}</p>;
  if (!rows) return <p className="hub-hint">Wczytywanie zapisów Bottle clickera...</p>;

  const needle = query.trim().toLowerCase();
  const visible = needle
    ? rows.filter((row) => row.username.toLowerCase().includes(needle))
    : rows;

  return (
    <Card title="Bottle clicker" icon="🍾">
      <p className="admin-muted">
        Prestiż, kaucja i sklep na koncie. Kwoty w złotych. Zapis przebija starą kopię w przeglądarce
        gracza.
      </p>
      <div className="admin-toolbar">
        <Field label="Szukaj">
          <input
            className="z-input"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Nick"
          />
        </Field>
        <Button size="sm" variant="danger" disabled={busy} onClick={() => void restartAll()}>
          Wyzeruj wszystkich
        </Button>
      </div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Gracz</th>
              <th>Restarty</th>
              <th>Portfel zł</th>
              <th>Łącznie zł</th>
              <th>Ten bieg zł</th>
              <th>But.</th>
              <th>Seria</th>
              <th>Sklep</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const draft = drafts[row.userId] ?? draftFromRow(row);
              return (
                <tr key={row.userId}>
                  <td>
                    <strong>{row.username}</strong>
                  </td>
                  <td>
                    <input
                      className="z-input"
                      type="number"
                      min={LIMITS.adminBottlePrestige.min}
                      max={LIMITS.adminBottlePrestige.max}
                      value={draft.prestigeCount}
                      onChange={(event) =>
                        patchDraft(row.userId, { prestigeCount: event.target.value })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="z-input"
                      type="number"
                      min={0}
                      step="0.01"
                      value={draft.groszeZl}
                      onChange={(event) => patchDraft(row.userId, { groszeZl: event.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="z-input"
                      type="number"
                      min={0}
                      step="0.01"
                      value={draft.lifetimeZl}
                      onChange={(event) =>
                        patchDraft(row.userId, { lifetimeZl: event.target.value })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="z-input"
                      type="number"
                      min={0}
                      step="0.01"
                      value={draft.runZl}
                      onChange={(event) => patchDraft(row.userId, { runZl: event.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="z-input"
                      type="number"
                      min={LIMITS.adminBottleReturned.min}
                      value={draft.returnedBottles}
                      onChange={(event) =>
                        patchDraft(row.userId, { returnedBottles: event.target.value })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="z-input"
                      type="number"
                      min={LIMITS.adminBottleCombo.min}
                      max={LIMITS.adminBottleCombo.max}
                      value={draft.bestCombo}
                      onChange={(event) =>
                        patchDraft(row.userId, { bestCombo: event.target.value })
                      }
                    />
                  </td>
                  <td>
                    <div className="admin-shop-levels">
                      {BOTTLE_CLICKER_UPGRADES.map((item) => (
                        <label key={item.id} title={item.label}>
                          <span aria-hidden="true">{item.icon}</span>
                          <input
                            className="z-input"
                            type="number"
                            min={0}
                            max={upgradeMaxLevel(item.id)}
                            value={draft.upgrades[item.id]}
                            onChange={(event) =>
                              patchDraft(row.userId, {
                                upgrades: { ...draft.upgrades, [item.id]: event.target.value },
                              })
                            }
                          />
                        </label>
                      ))}
                    </div>
                  </td>
                  <td>
                    <Button size="sm" disabled={busy} onClick={() => void saveRow(row)}>
                      Zapisz
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {visible.length === 0 ? <p className="hub-hint">Brak kont pasujących do wyszukiwania.</p> : null}
    </Card>
  );
}
